import React, { createContext, ReactNode, useState } from "react";
import { IQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/Domain/types";

/**
 * Interface for managing the current questionnaire ID
 */
export interface IUseCurrentQuestionnaireId {
    currentQuestionnaireId: IQuestionnaireDetails["questionnaireId"] | undefined;
    setCurrentQuestionnaireId: (questionnaireId?: IQuestionnaireDetails["questionnaireId"]) => void;
}

/**
 * Interface for managing the current questionnaire details
 */
export interface IUseCurrentQuestionnaireDetails {
    currentQuestionnaireDetails: IQuestionnaireDetails | undefined;
    setCurrentQuestionnaireDetails: (details?: IQuestionnaireDetails) => void;
}

/** Combined interface for the questionnaire context */
type IQuestionnaireContext = IUseCurrentQuestionnaireId & IUseCurrentQuestionnaireDetails;

/** Default values for the questionnaire context */
const DEFAULT_QUESTIONNAIRE_CONTEXT: IQuestionnaireContext = {
    currentQuestionnaireId: undefined,
    setCurrentQuestionnaireId: () => {},
    currentQuestionnaireDetails: undefined,
    setCurrentQuestionnaireDetails: () => {},
};

/**
 * @description Provides a React context for managing questionnaire state
 */
export const QuestionnaireContext = createContext<IQuestionnaireContext>(DEFAULT_QUESTIONNAIRE_CONTEXT);

/**
 * Provider component for the QuestionnaireContext
 */
export const QuestionnaireProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentQuestionnaireId, setCurrentQuestionnaireId] = useState<
        IQuestionnaireDetails["questionnaireId"] | undefined
    >(undefined);

    const [currentQuestionnaireDetails, setCurrentQuestionnaireDetails] = useState<IQuestionnaireDetails | undefined>(
        undefined
    );

    const value: IQuestionnaireContext = {
        currentQuestionnaireId,
        setCurrentQuestionnaireId,
        currentQuestionnaireDetails,
        setCurrentQuestionnaireDetails,
    };

    return <QuestionnaireContext.Provider value={value}>{children}</QuestionnaireContext.Provider>;
};
